<script setup>
  import { computed, ref, onMounted, onUnmounted, watch, watchEffect } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useMapStore } from "@/stores/MapStore";
  import { useMapAnimationStore } from "@/stores/MapAnimationStore";
  import { useDateHelper } from "@/composables/useDateHelper";
  import RangeSlider from "@/components/controls/RangeSlider.vue";
  import Icon from "@/components/icons/Icon.vue";
  import { formatInTimeZone } from 'date-fns-tz';

  const props = defineProps({
    isSmallDevice: {
      type: Boolean,
      required: true
    }
  });

  const mapAnimationStore = useMapAnimationStore();
  const mapStore = useMapStore();

  let { animationFrames, displayAnimationControls, currentFrame, currentFrameTimestamp, frameCount, isAnimating, isLoading, loadingMessage, displayNowLine, resumeAnimation } = storeToRefs(mapAnimationStore);

  const dateHelper = useDateHelper();

  if (!mapStore.state.geoInfo) await mapStore.getMapGeoInfo();

  const timeFromNow = ref('Loading');
  const dragStartListener = ref(null);
  const dragEndListener = ref(null);

  const timestep = computed(() => {
    if (isLoading.value || !currentFrameTimestamp.value) return 'Loading';
    
    const date = new Date(currentFrameTimestamp.value);
    
    const formattedDate = formatInTimeZone(date, mapStore.state.geoInfo.local_utc_offset, 'ccc, LLL d | h:mm a');

    return `${formattedDate} ${mapStore.state.geoInfo.local_timezone_short}`;
  });

  const playButtonAccessibilityText = computed(() => !isAnimating.value ? 'Play map animation' : 'Pause map animation')
  const maxFrameCount = computed(() => frameCount.value - 1 < 0 ? 0 : frameCount.value - 1);
  const showAnimationControls = computed(() => mapStore.state.hasOwnProperty('animationControlsNeeded') ? mapStore.state.animationControlsNeeded : displayAnimationControls.value);
  const showPlayButton = computed(() => !isAnimating.value);
  const timeFromNowText = computed(() => isLoading.value ? 'Loading' : timeFromNow.value );
  const timestampClasses = computed(() => ['jwx-timeline__time', { 'jwx-timeline__time--long': !isLoading.value && timestep.value.length > 26 }]);
  const sliderStyles = computed(() => ({ 
    '--now-marker-position': `${nowPositionPercent.value}%`
  }));  

  const nowPositionPercent = computed(() => {
    if (frameCount.value === 0) return 0;

    const now = new Date();
    const timeA = animationFrames.value[0];
    const timeB = animationFrames.value[frameCount.value - 1];

    const timespanToNow = getTimeSpan(timeA, now)
    const timespanTotal = getTimeSpan(timeA, timeB)
    
    return (timespanToNow / timespanTotal) * 100;
  });

  function handleAnimationFrameChange() {
    window.$EventBus.dispatchEvent('animation-slider-changed', { currentFrame: parseInt(currentFrame.value), animate: isAnimating.value, timeseries: true });
  }

  function handlePlayTimeline() {
    mapAnimationStore.toggleAnimationStatus();
  }

  function handleStepForward() {
    currentFrame.value = currentFrame.value + 1 >= frameCount.value ? 0 : currentFrame.value + 1;
    handleAnimationFrameChange();
  }

  function handleStepBack() {
    currentFrame.value = currentFrame.value - 1 < 0 ? frameCount.value - 1 : currentFrame.value - 1;
    handleAnimationFrameChange();
  }

  function handleDragstart() {
    if (isAnimating.value) {
      mapAnimationStore.toggleAnimationStatus({ resume: true });
    }    
  }

  function handleDragend() {
    let goToStartFrame = mapStore.state.obsOverlays.has('markers') || mapStore.state.fxOverlays.has('markers');
    
    if (resumeAnimation.value) {
      mapAnimationStore.toggleAnimationStatus({ restart: goToStartFrame });
    }

    if (goToStartFrame) mapAnimationStore.goToStartFrame();
  }

  function getTimeSpan(timestampA, timestampB) {
    return Math.abs(new Date(timestampA).getTime() - new Date(timestampB).getTime())
  }

  function setAnimationStatus(e) {
    isAnimating.value = e.detail.animate
  }

  function setTimeFromNow() {
    if (!currentFrameTimestamp.value) return;
    
    timeFromNow.value = dateHelper.timeFromNow(currentFrameTimestamp.value);
  }

  function handleKeyPress(e) {
    const key = e.key; // Get the key that was pressed
    const code = e.code; // Get the physical key code
    const activeElementId = document.activeElement?.id; // Get the currently focused element

    if (activeElementId === 'map-animation-slider') return; // Ignore key presses if the slider is focused

    // Check for specific keys
    if (key === 'p') {
      handlePlayTimeline();
    } else if (code === 'ArrowRight') {
      handleStepForward();
    } else if (code === 'ArrowLeft') {
      handleStepBack();
    }
  }

  onMounted(() => {
    window.$EventBus.addEventListener('timeline-status-change', setAnimationStatus);

    dragStartListener.value = google.maps.event.addListener(window.$JWX.map, 'dragstart', handleDragstart);
    dragEndListener.value = google.maps.event.addListener(window.$JWX.map, 'dragend', handleDragend);

    setTimeFromNow();
    setInterval(setTimeFromNow, 10000);
  })
  
  onUnmounted(() => {
    window.$EventBus.removeEventListener('timeline-status-change', setAnimationStatus);
    google.maps.event.removeListener(dragStartListener.value);
    google.maps.event.removeListener(dragEndListener.value);

    if (showAnimationControls.value) {
      document.removeListener('keydown', handleKeyPress)
      window.$JWX.map.setOptions({ keyboardShortcuts: false });
    };
  })

  watchEffect(() => {
    currentFrameTimestamp.value && setTimeFromNow();
  })

  watchEffect(() => {
    if (showAnimationControls.value) {
      document.addEventListener('keydown', handleKeyPress)
      window.$JWX.map.setOptions({ keyboardShortcuts: false });
    };
  })

  watch(mapStore.state, (newValue) => {
    if (newValue.obsOverlays.has('markers') || newValue.fxOverlays.has('markers')) {
      mapAnimationStore.goToStartFrame();
    }
  })
</script>
<template>
  <Teleport to=".jwx-map__bottom-container" :disabled="!props.isSmallDevice">
    <div v-if="showAnimationControls"  class="jwx-map__timeline jwx-map-block jwx-map-block--transparent-border" data-map="timeline">
      <div class="jwx-map-block__content">
        <div class="jwx-timeline" :key="mapStore.state.name">
          <div :class="timestampClasses">
            <p class="jwx-timeline__timestamp">{{ isLoading ? loadingMessage : timestep }}</p> 
            <p v-html="timeFromNowText" class="jwx-timeline__time-offset"></p>
          </div>
          <div class="jwx-timeline__controls">
            <button
              @click.prevent="handleStepBack"
              class="jwx-timeline__control-button jwx-timeline__step-button">
              <Icon name="step-back-solid" />
              <span class="sr-only">Step back one frame</span>
            </button>            
            <button
              @click.prevent="handlePlayTimeline"
              class="jwx-timeline__control-button jwx-timeline__play-button">
              <Icon v-if="showPlayButton" name="play" />
              <Icon v-else name="paused" />
              <span class="sr-only">{{ playButtonAccessibilityText }}</span>
            </button>
            <button
              @click.prevent="handleStepForward"
              class="jwx-timeline__control-button jwx-timeline__step-button">
              <Icon name="step-forward-solid" />
              <span class="sr-only">Step forward one frame</span>
            </button>
          </div>
          <RangeSlider
            @change="handleAnimationFrameChange"
            @keyup.space="handlePlayTimeline"
            id="map-animation-slider"
            class="jwx-timeline__slider"
            :min="0"
            :max="maxFrameCount"
            v-model="mapAnimationStore.currentFrame"
            :style="sliderStyles"
            :data-has-nowline="displayNowLine"
            :data-loading="isLoading"
            :show-ticks="true"
            :disabled="isLoading"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>